// src/pages/gobierno-cloud/CloudAccountsPage.js
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Database,
  Edit2,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
  CheckCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
const BASE = `${API_URL}/gobierno-cloud/cloud-accounts`;

// ─── paleta Gobierno Cloud (sky blue) ────────────────────────────────────────
const ACCENT   = '#0ea5e9';
const ACCENT_DIM = 'rgba(14,165,233,0.18)';
const BORDER   = 'rgba(14,165,233,0.25)';

const CLOUD_BADGE = {
  AZURE:   { bg: 'rgba(56,189,248,0.18)',  color: '#38bdf8', label: 'Azure'  },
  AWS:     { bg: 'rgba(251,146,60,0.18)',  color: '#fb923c', label: 'AWS'    },
  GCP:     { bg: 'rgba(52,211,153,0.18)',  color: '#34d399', label: 'GCP'    },
  HUAWEI:  { bg: 'rgba(248,113,113,0.18)', color: '#f87171', label: 'Huawei' },
};

const STATUS_BADGE = {
  ACTIVE:   { bg: 'rgba(74,222,128,0.18)',  color: '#4ade80', label: 'Activa'   },
  INACTIVE: { bg: 'rgba(156,163,175,0.18)', color: '#9ca3af', label: 'Inactiva' },
};

const EMPTY_FORM = {
  cliente_id: '',
  cloud: 'AZURE',
  scope_type: 'SUBSCRIPTION',
  scope_id: '',
  scope_name: '',
  display_name: '',
  parent_cloud_account_id: '',
  environment: 'PROD',
  organization: '',
  status: 'ACTIVE',
  discovered_by: 'MANUAL',
};

const PAGE_SIZE = 25;

// ─── helpers ─────────────────────────────────────────────────────────────────

function Badge({ value, map }) {
  const cfg = map[value] || { bg: 'rgba(100,100,100,0.2)', color: '#ccc', label: value };
  return (
    <span
      className="px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: cfg.bg, color: cfg.color }}
    >
      {cfg.label}
    </span>
  );
}

function Input({ label, name, value, onChange, type = 'text', required, hint }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-sky-200/80">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        className="rounded-lg px-3 py-2 text-sm bg-white/5 border border-sky-500/20 text-white placeholder-white/30 focus:outline-none focus:border-sky-400/60 focus:bg-white/8"
        placeholder={hint || ''}
      />
    </div>
  );
}

function Select({ label, name, value, onChange, options, required }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-sky-200/80">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        className="rounded-lg px-3 py-2 text-sm bg-[#0c1a2e] border border-sky-500/20 text-white focus:outline-none focus:border-sky-400/60"
      >
        {options.map(o => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

function AccountModal({ mode, account, enums, onClose, onSave }) {
  const [form, setForm] = useState(() =>
    mode === 'edit' && account
      ? { ...EMPTY_FORM, ...Object.fromEntries(Object.entries(account).map(([k, v]) => [k, v ?? ''])) }
      : { ...EMPTY_FORM }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.cloud || !form.scope_type || !form.scope_id) {
      setError('Cloud, Scope Type y Scope ID son obligatorios.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave(form);
    } catch (err) {
      setError(err.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
    >
      <div
        className="relative w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(135deg,#071727 0%,#0a1e35 50%,#071727 100%)',
          border: `1px solid ${BORDER}`,
        }}
      >
        {/* header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-sky-500/15">
          <div className="flex items-center gap-2">
            <Database size={18} style={{ color: ACCENT }} />
            <span className="font-semibold text-white">
              {mode === 'create' ? 'Nueva Cuenta Cloud' : 'Editar Cuenta Cloud'}
            </span>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* form */}
        <form onSubmit={handleSubmit} className="p-6 grid grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
          <Input label="Display Name" name="display_name" value={form.display_name} onChange={handleChange} hint="Nombre amigable" />
          <Input label="Cliente ID" name="cliente_id" value={form.cliente_id} onChange={handleChange} hint="ID del cliente" />
          <Select label="Cloud" name="cloud" value={form.cloud} onChange={handleChange} options={enums.cloud} required />
          <Select label="Scope Type" name="scope_type" value={form.scope_type} onChange={handleChange} options={enums.scope_type} required />
          <Input label="Scope ID" name="scope_id" value={form.scope_id} onChange={handleChange} required hint="ID real del proveedor" />
          <Input label="Scope Name" name="scope_name" value={form.scope_name} onChange={handleChange} hint="Nombre del proveedor" />
          <Input label="Organización" name="organization" value={form.organization} onChange={handleChange} />
          <Select label="Environment" name="environment" value={form.environment} onChange={handleChange} options={['', ...enums.environment]} />
          <Select label="Status" name="status" value={form.status} onChange={handleChange} options={enums.status} required />
          <Select label="Descubierto por" name="discovered_by" value={form.discovered_by} onChange={handleChange} options={enums.discovered_by} />
          <div className="col-span-2">
            <Input
              label="Parent Cloud Account ID"
              name="parent_cloud_account_id"
              value={form.parent_cloud_account_id}
              onChange={handleChange}
              hint="UUID del scope padre (opcional)"
            />
          </div>

          {error && (
            <div className="col-span-2 flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-300 bg-red-500/10 border border-red-500/20">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          <div className="col-span-2 flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm border border-white/15 text-white/60 hover:text-white hover:border-white/30 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-50"
              style={{ background: ACCENT_DIM, border: `1px solid ${ACCENT}55` }}
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
              {saving ? 'Guardando…' : mode === 'create' ? 'Crear Cuenta' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── DeleteConfirm ────────────────────────────────────────────────────────────

function DeleteConfirm({ account, onClose, onConfirm, deleting }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
    >
      <div
        className="w-full max-w-sm rounded-2xl p-6 shadow-2xl"
        style={{ background: '#0a1e35', border: '1px solid rgba(239,68,68,0.3)' }}
      >
        <div className="flex items-center gap-2 mb-3 text-red-400">
          <AlertCircle size={18} />
          <span className="font-semibold">Eliminar Cuenta</span>
        </div>
        <p className="text-sm text-white/70 mb-1">
          ¿Confirmas eliminar la cuenta?
        </p>
        <p className="text-sm font-medium text-white mb-4">
          {account.display_name || account.scope_name || account.cloud_account_id}
        </p>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-white/15 text-white/60 hover:text-white transition-colors">
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30 transition-all disabled:opacity-50"
          >
            {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
            {deleting ? 'Eliminando…' : 'Eliminar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────

function Toast({ msg, type, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, []);
  const isOk = type === 'ok';
  return (
    <div
      className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium shadow-2xl"
      style={{
        background: isOk ? 'rgba(74,222,128,0.15)' : 'rgba(239,68,68,0.15)',
        border: isOk ? '1px solid rgba(74,222,128,0.4)' : '1px solid rgba(239,68,68,0.4)',
        color: isOk ? '#4ade80' : '#f87171',
      }}
    >
      {isOk ? <CheckCircle size={15} /> : <AlertCircle size={15} />}
      {msg}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function CloudAccountsPage() {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('gobierno_cloud.cloud_accounts.create');
  const canEdit   = hasPermission('gobierno_cloud.cloud_accounts.edit');
  const canDelete = hasPermission('gobierno_cloud.cloud_accounts.delete');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [filters, setFilters] = useState({ cloud: '', scope_type: '', status: '', environment: '', search: '' });
  const [offset, setOffset] = useState(0);

  const [enums, setEnums] = useState({
    cloud: ['AZURE', 'AWS', 'GCP', 'HUAWEI'],
    scope_type: ['TENANT', 'SUBSCRIPTION', 'ACCOUNT', 'PROJECT'],
    environment: ['PROD', 'QA', 'DEV', 'TEST', 'SANDBOX'],
    status: ['ACTIVE', 'INACTIVE'],
    discovered_by: ['MANUAL', 'ETL_AZURE', 'ETL_AWS', 'ETL_GCP'],
  });

  const [modal, setModal] = useState(null); // { mode: 'create'|'edit', account? }
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null); // { msg, type }

  const showToast = (msg, type = 'ok') => setToast({ msg, type });

  // ── fetch enums once ─────────────────────────────────────────────────────
  useEffect(() => {
    fetch(`${BASE}/meta/enums`)
      .then(r => r.json())
      .then(data => setEnums(data))
      .catch(() => {});
  }, []);

  // ── fetch rows ────────────────────────────────────────────────────────────
  const fetchRows = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true); else setRefreshing(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', PAGE_SIZE);
      params.set('offset', offset);
      if (filters.cloud)      params.set('cloud', filters.cloud);
      if (filters.scope_type) params.set('scope_type', filters.scope_type);
      if (filters.status)     params.set('status', filters.status);
      if (filters.environment) params.set('environment', filters.environment);
      if (filters.search)     params.set('search', filters.search);

      const res = await fetch(`${BASE}/?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setRows(json.data || []);
      setTotal(json.total || 0);
    } catch (err) {
      showToast('Error cargando cuentas: ' + err.message, 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters, offset]);

  useEffect(() => { fetchRows(true); }, [filters, offset]);

  // ── create / edit ─────────────────────────────────────────────────────────
  const handleSave = async (form) => {
    const isEdit = modal.mode === 'edit';
    const url  = isEdit ? `${BASE}/${modal.account.cloud_account_id}` : `${BASE}/`;
    const method = isEdit ? 'PUT' : 'POST';

    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.detail || 'Error en servidor');

    showToast(isEdit ? 'Cuenta actualizada' : 'Cuenta creada');
    setModal(null);
    setOffset(0);
    fetchRows(false);
  };

  // ── delete ────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await fetch(`${BASE}/${deleteTarget.cloud_account_id}`, { method: 'DELETE' });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.detail || 'Error al eliminar');
      }
      showToast('Cuenta eliminada');
      setDeleteTarget(null);
      setOffset(0);
      fetchRows(false);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ── pagination ────────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  // ── filter change resets page ─────────────────────────────────────────────
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(f => ({ ...f, [name]: value }));
    setOffset(0);
  };

  const clearFilters = () => {
    setFilters({ cloud: '', scope_type: '', status: '', environment: '', search: '' });
    setOffset(0);
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen p-6 font-sans"
      style={{
        background:
          'radial-gradient(circle at top right,rgba(14,165,233,0.15),transparent 35%),' +
          'radial-gradient(circle at bottom left,rgba(56,189,248,0.08),transparent 30%),' +
          'linear-gradient(135deg,#040e1a 0%,#071727 55%,#040e1a 100%)',
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: ACCENT_DIM, border: `1px solid ${BORDER}` }}
          >
            <img
              src="/assets/icons/KIO_Cloud_Data.png"
              alt="cloud"
              className="w-6 h-6 object-contain"
              style={{ filter: 'brightness(0) saturate(100%) invert(55%) sepia(80%) saturate(500%) hue-rotate(175deg) brightness(110%)' }}
            />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white leading-tight">Cloud Accounts</h1>
            <p className="text-xs text-sky-300/60">Catálogo maestro · <span style={{ color: ACCENT }}>{total}</span> registros</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchRows(false)}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs border border-sky-500/20 text-sky-300/70 hover:text-sky-200 hover:border-sky-400/35 transition-all"
          >
            <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
            Actualizar
          </button>

          {canCreate && (
            <button
              onClick={() => setModal({ mode: 'create' })}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white transition-all hover:scale-105"
              style={{ background: ACCENT_DIM, border: `1px solid ${ACCENT}55` }}
            >
              <Plus size={14} style={{ color: ACCENT }} />
              Nueva Cuenta
            </button>
          )}
        </div>
      </div>

      {/* ── Filtros ─────────────────────────────────────────────────────────── */}
      <div
        className="rounded-2xl p-4 mb-5 flex flex-wrap items-end gap-3"
        style={{ background: 'rgba(14,165,233,0.05)', border: `1px solid ${BORDER}` }}
      >
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-sky-400/50" />
          <input
            name="search"
            value={filters.search}
            onChange={handleFilterChange}
            placeholder="Buscar por nombre…"
            className="w-full pl-8 pr-3 py-2 rounded-xl text-sm bg-white/5 border border-sky-500/20 text-white placeholder-white/30 focus:outline-none focus:border-sky-400/50"
          />
        </div>

        {/* Cloud */}
        <select
          name="cloud"
          value={filters.cloud}
          onChange={handleFilterChange}
          className="px-3 py-2 rounded-xl text-sm bg-[#0c1a2e] border border-sky-500/20 text-white focus:outline-none focus:border-sky-400/50 min-w-[110px]"
        >
          <option value="">Todos los clouds</option>
          {enums.cloud.map(v => <option key={v} value={v}>{v}</option>)}
        </select>

        {/* Scope Type */}
        <select
          name="scope_type"
          value={filters.scope_type}
          onChange={handleFilterChange}
          className="px-3 py-2 rounded-xl text-sm bg-[#0c1a2e] border border-sky-500/20 text-white focus:outline-none focus:border-sky-400/50 min-w-[130px]"
        >
          <option value="">Todos los scopes</option>
          {enums.scope_type.map(v => <option key={v} value={v}>{v}</option>)}
        </select>

        {/* Status */}
        <select
          name="status"
          value={filters.status}
          onChange={handleFilterChange}
          className="px-3 py-2 rounded-xl text-sm bg-[#0c1a2e] border border-sky-500/20 text-white focus:outline-none focus:border-sky-400/50 min-w-[110px]"
        >
          <option value="">Todos los status</option>
          {enums.status.map(v => <option key={v} value={v}>{v}</option>)}
        </select>

        {/* Environment */}
        <select
          name="environment"
          value={filters.environment}
          onChange={handleFilterChange}
          className="px-3 py-2 rounded-xl text-sm bg-[#0c1a2e] border border-sky-500/20 text-white focus:outline-none focus:border-sky-400/50 min-w-[110px]"
        >
          <option value="">Todos los envs</option>
          {enums.environment.map(v => <option key={v} value={v}>{v}</option>)}
        </select>

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-1 px-3 py-2 rounded-xl text-xs border border-white/15 text-white/50 hover:text-white hover:border-white/30 transition-colors"
          >
            <X size={12} /> Limpiar
          </button>
        )}
      </div>

      {/* ── Tabla ───────────────────────────────────────────────────────────── */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{ border: `1px solid ${BORDER}`, background: 'rgba(4,14,26,0.7)', backdropFilter: 'blur(12px)' }}
      >
        {loading ? (
          <div className="flex items-center justify-center py-24 gap-3 text-sky-300/60">
            <Loader2 size={22} className="animate-spin" style={{ color: ACCENT }} />
            <span className="text-sm">Cargando cuentas…</span>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <Database size={40} className="opacity-20" style={{ color: ACCENT }} />
            <p className="text-sm text-white/40">No hay cuentas con los filtros actuales</p>
            {canCreate && (
              <button
                onClick={() => setModal({ mode: 'create' })}
                className="mt-2 px-4 py-2 rounded-xl text-xs font-medium text-white"
                style={{ background: ACCENT_DIM, border: `1px solid ${ACCENT}55` }}
              >
                Crear primera cuenta
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: 'rgba(14,165,233,0.07)', borderBottom: `1px solid ${BORDER}` }}>
                  {['Display Name', 'Cloud', 'Scope Type', 'Scope ID', 'Cliente', 'Environment', 'Status', 'Descubierto', 'Actualizado', ''].map(h => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide"
                      style={{ color: 'rgba(125,211,252,0.7)' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr
                    key={row.cloud_account_id}
                    className="transition-colors hover:bg-sky-400/5"
                    style={{ borderBottom: i < rows.length - 1 ? '1px solid rgba(14,165,233,0.08)' : 'none' }}
                  >
                    <td className="px-4 py-3 font-medium text-white/90 max-w-[180px] truncate" title={row.display_name}>
                      {row.display_name || <span className="text-white/30 italic">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <Badge value={row.cloud} map={CLOUD_BADGE} />
                    </td>
                    <td className="px-4 py-3 text-white/70 text-xs font-mono">{row.scope_type}</td>
                    <td className="px-4 py-3 text-white/60 text-xs font-mono max-w-[160px] truncate" title={row.scope_id}>
                      {row.scope_id}
                    </td>
                    <td className="px-4 py-3 text-white/60 text-xs">{row.cliente_id || <span className="text-white/25">—</span>}</td>
                    <td className="px-4 py-3 text-white/60 text-xs">{row.environment || <span className="text-white/25">—</span>}</td>
                    <td className="px-4 py-3">
                      <Badge value={row.status} map={STATUS_BADGE} />
                    </td>
                    <td className="px-4 py-3 text-white/50 text-xs">{row.discovered_by || <span className="text-white/25">—</span>}</td>
                    <td className="px-4 py-3 text-white/40 text-xs whitespace-nowrap">
                      {row.updated_at ? new Date(row.updated_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {canEdit && (
                          <button
                            onClick={() => setModal({ mode: 'edit', account: row })}
                            className="p-1.5 rounded-lg text-sky-400/50 hover:text-sky-300 hover:bg-sky-400/10 transition-colors"
                            title="Editar"
                          >
                            <Edit2 size={13} />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => setDeleteTarget(row)}
                            className="p-1.5 rounded-lg text-red-400/50 hover:text-red-300 hover:bg-red-400/10 transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && total > PAGE_SIZE && (
          <div className="flex items-center justify-between px-5 py-3 border-t" style={{ borderColor: BORDER }}>
            <span className="text-xs text-white/40">
              Mostrando {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} de {total}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))}
                disabled={currentPage === 1}
                className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/5 disabled:opacity-30 transition-colors"
              >
                <ChevronLeft size={15} />
              </button>
              <span className="text-xs text-white/50 px-2">
                Página {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setOffset(o => o + PAGE_SIZE)}
                disabled={currentPage >= totalPages}
                className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/5 disabled:opacity-30 transition-colors"
              >
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Modals ──────────────────────────────────────────────────────────── */}
      {modal && (
        <AccountModal
          mode={modal.mode}
          account={modal.account}
          enums={enums}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}

      {deleteTarget && (
        <DeleteConfirm
          account={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}

      {toast && (
        <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />
      )}
    </div>
  );
}
